import { NgClass, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { TopicLevel1Query, TopicLevel2Query } from '@examdojo/category/v2';
import { Icon, IconComponent } from '@examdojo/core/icon';
import { MatTooltipTruncateDirective } from '@examdojo/core/mat-tooltip-truncate';
import { provideFaIcons } from '@examdojo/icons';
import { STEM_DIFFICULTY_CATEGORY_TO_CARD_ICON, StemDifficultyCategory } from '@examdojo/models/question';
import { PracticeActivityCandidateStoreModel } from '@examdojo/practice-activity';
import { ImageComponent } from '@examdojo/core/ui/image';
import { LoaderComponent } from '@examdojo/core/loader';
import { entries } from '@examdojo/util/object-utils';
import { isNotNullish } from '@examdojo/util/nullish';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, map } from 'rxjs';
import { QuestionSelectionProficiencyBarComponent } from './question-selection-card-bar.component';
import {
  QuestionSelectionTopicsListComponent,
  QuestionSelectionTopicsListComponentItem,
} from './question-selection-topics-list.component';

@Component({
  selector: 'dojo-question-selection-card',
  templateUrl: './question-selection-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    IconComponent,
    ImageComponent,
    QuestionSelectionProficiencyBarComponent,
    QuestionSelectionTopicsListComponent,
    TranslocoPipe,
    LoaderComponent,
    PercentPipe,
    MatTooltip,
    MatTooltipTruncateDirective,
  ],
  styleUrls: ['./question-selection-card.component.scss'],
})
export class QuestionSelectionCardComponent {
  constructor(
    private readonly topicLevel2Query: TopicLevel2Query,
    private readonly topicLevel1Query: TopicLevel1Query,
  ) {
    provideFaIcons([faCheck]);
  }

  readonly STEM_DIFFICULTY_CATEGORY_TO_CARD_ICON = STEM_DIFFICULTY_CATEGORY_TO_CARD_ICON;

  readonly isSelected = input.required<boolean>();
  readonly questionCandidate = input.required<QuestionCandidate>();

  readonly questionCard$ = combineLatest([
    toObservable(this.questionCandidate),
    this.topicLevel2Query.entities$,
    this.topicLevel1Query.entities$,
  ]).pipe(
    map(([questionCandidate]) => {
      const listOfTopicLevel2 = entries(questionCandidate.metadata?.topics_level_02_proficiencies ?? {})
        .map(([topicLevel2Id, proficiency]) => {
          const topicLevel2 = this.topicLevel2Query.getEntity(+topicLevel2Id);
          const topicLevel1 = topicLevel2?.topic_level_01_id
            ? this.topicLevel1Query.getEntity(topicLevel2?.topic_level_01_id)
            : null;

          return {
            topicLevel2Id,
            proficiency,
            topicLevel2,
            topicLevel1,
            icon: {
              name: topicLevel2?.icon as Icon,
              color: topicLevel1?.color_name,
            },
          } as QuestionSelectionTopicsListComponentItem;
        })
        .filter((item) => isNotNullish(item.topicLevel2));
      const questionDifficulty = questionCandidate.difficulty;

      const value = {
        questionCandidate,
        totalMarks: questionCandidate.totalMarks,
        listOfTopicLevel2,
        questionDifficulty,
        proficiency: {
          priorScore: questionCandidate.metadata ? questionCandidate.metadata.prior_predicted_score : 0,
          predictedScore: questionCandidate.metadata ? questionCandidate.metadata.predicted_score : 0,
          gain: questionCandidate.metadata
            ? (questionCandidate.metadata.predicted_score - questionCandidate.metadata.prior_predicted_score) /
              questionCandidate.metadata.prior_predicted_score
            : 0,
        },
      };

      return value;
    }),
  );

  readonly state = connectState({
    questionCard: this.questionCard$,
    isLoadingTopics: combineLatest([this.topicLevel1Query.selectLoading(), this.topicLevel2Query.selectLoading()]).pipe(
      map(([tl1, tl2]) => tl1 || tl2),
    ),
  });
}

export interface QuestionCandidate extends PracticeActivityCandidateStoreModel {
  totalMarks: number;
  difficulty: StemDifficultyCategory;
}
